import { RefObject } from 'react'

import { isWindow } from './browser'
import { getFirstFocusableElement } from './dom'

export type ScrollMotionDuration = Readonly<{ maxDistance: number; duration: number }>

export type ScrollOptions = {
  delay: number
  motionDurations: ReadonlyArray<ScrollMotionDuration>
  motionMaxDuration: number
}

/**
 * Calcula a duração da animação de scroll com base na distância percorrida.
 * Usado no lugar de uma duração fixa para que scrolls curtos sejam rápidos
 * e scrolls longos não pareçam lentos demais.
 */
export function getScrollDuration(
  distance: number,
  motionDurations: ReadonlyArray<ScrollMotionDuration>,
  maxDuration: number
): number {
  for (const threshold of motionDurations) {
    if (Math.abs(distance) <= threshold.maxDistance) return threshold.duration
  }
  return maxDuration
}

/**
 * Função de easing cúbico que desacelera o scroll no final da animação.
 * Usar quando for necessário controlar a duração da animação ou combinar scroll + foco
 * sem conflitos, o `behavior: 'smooth'` nativo não expõe essas opções.
 */
export function easeOut(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

/**
 * Sobe na árvore do DOM, a partir do próprio elemento, até encontrar o ancestral
 * (ou o elemento em si) com scroll ativo. Necessário porque `scrollIntoView` e
 * `window.scrollTo` não funcionam corretamente quando o scroll está em um container
 * intermediário: eles assumem o `window` como alvo.
 */
export function findScrollableParent(element: HTMLElement): HTMLElement | Window {
  let current: HTMLElement | null = element
  while (current) {
    const { overflow, overflowY } = window.getComputedStyle(current)
    if (/auto|scroll/.test(overflow + overflowY)) return current
    current = current.parentElement
  }
  return window
}

/**
 * Anima o scroll de uma posição até outra usando `requestAnimationFrame` e {@link easeOut}.
 * O método nativo `behavior: 'smooth'` não aceita callback de conclusão nem
 * permite sincronizar o momento exato do foco com o fim da animação.
 */
export function animateScroll(container: HTMLElement | Window, from: number, to: number, duration: number): void {
  const startTime = performance.now()
  const distance = to - from

  function step(currentTime: number) {
    const elapsed = currentTime - startTime
    const progress = Math.min(elapsed / duration, 1)
    const position = from + distance * easeOut(progress)

    if (isWindow(container)) {
      container.scrollTo(0, position)
    } else {
      container.scrollTop = position
    }

    if (progress < 1) {
      requestAnimationFrame(step)
    }
  }

  requestAnimationFrame(step)
}

/**
 * Rola até o final do container com animação.
 * O delay existe para aguardar renderizações condicionais (ex: um novo campo que aparece
 * após uma resposta) antes de calcular a posição final, sem ele, o `scrollHeight`
 * ainda reflete o estado anterior ao render.
 */
export function scrollToBottom(containerRef: RefObject<HTMLElement>, options: ScrollOptions): void {
  setTimeout(() => {
    const element = containerRef?.current
    if (!element) return

    const container = findScrollableParent(element)
    const containerScrollTop = isWindow(container) ? window.scrollY : container.scrollTop
    const scrollHeight = isWindow(container) ? document.body.scrollHeight : container.scrollHeight
    const containerHeight = isWindow(container) ? window.innerHeight : container.clientHeight
    const targetScroll = scrollHeight - containerHeight
    const duration = getScrollDuration(
      targetScroll - containerScrollTop,
      options.motionDurations,
      options.motionMaxDuration
    )

    animateScroll(container, containerScrollTop, targetScroll, duration)
  }, options.delay)
}

/**
 * Aguarda o delay configurado e move o foco para o elemento, se ele existir e não estiver desabilitado.
 * O delay é necessário porque o elemento pode ter acabado de ficar habilitado por uma mudança de estado
 * do formulário (ex: erro de validação removido) que o React ainda não comitou no DOM no momento do
 * disparo — sem esperar, `element.disabled` refletiria o estado anterior à mudança.
 */
export function focusElementAfterDelay(
  element: HTMLElement | null | undefined,
  options: Pick<ScrollOptions, 'delay'>
): void {
  setTimeout(() => {
    if (element && !element.matches(':disabled')) {
      element.focus({ preventScroll: true })
    }
  }, options.delay)
}

/**
 * Calcula a posição de scroll que alinha o elemento na viewport conforme `block`
 * (`start`: topo do elemento encostado no topo do container; `center`: elemento centralizado,
 * equivalente ao `block` do `scrollIntoView` nativo).
 */
export function getTargetScroll(
  containerScrollTop: number,
  containerHeight: number,
  containerTop: number,
  elementRect: Pick<DOMRect, 'top' | 'height'>,
  block: 'start' | 'center'
): number {
  return block === 'start'
    ? containerScrollTop + elementRect.top - containerTop
    : containerScrollTop + elementRect.top - containerTop - (containerHeight - elementRect.height) / 2
}

/**
 * Alinha o elemento na viewport (`start`: topo do elemento; `center`: elemento centralizado,
 * equivalente ao `block` do `scrollIntoView` nativo) e move o foco para o próximo campo interativo.
 * Usar quando for necessário controlar esse alinhamento (o `scrollIntoView` só garante visibilidade)
 * ou mover o foco sem disparar o scroll nativo do browser por cima da animação.
 */
export function scrollAndFocusElement(
  elementToScrollId: string,
  options: ScrollOptions,
  focusOverride?: HTMLElement | null,
  onFocused?: (target: HTMLElement) => void,
  block: 'start' | 'center' = 'center'
): void {
  setTimeout(() => {
    const element = document.getElementById(elementToScrollId)
    if (!element) return

    const container = findScrollableParent(element)
    const containerScrollTop = isWindow(container) ? window.scrollY : container.scrollTop
    const containerHeight = isWindow(container) ? window.innerHeight : container.clientHeight
    const containerTop = isWindow(container) ? 0 : container.getBoundingClientRect().top
    const elementRect = element.getBoundingClientRect()

    const targetScroll = getTargetScroll(containerScrollTop, containerHeight, containerTop, elementRect, block)

    const duration = getScrollDuration(
      targetScroll - containerScrollTop,
      options.motionDurations,
      options.motionMaxDuration
    )

    const isContainerScrollable = isWindow(container)
      ? document.body.scrollHeight > window.innerHeight
      : container.scrollHeight > container.clientHeight

    const availableFocusOverride = focusOverride?.matches(':disabled') ? undefined : focusOverride

    const focusTarget = isContainerScrollable
      ? availableFocusOverride ?? getFirstFocusableElement(element)
      : getFirstFocusableElement(element) ?? availableFocusOverride

    if (focusTarget) {
      focusTarget.focus({ preventScroll: true })
      onFocused?.(focusTarget)
    }

    animateScroll(container, containerScrollTop, targetScroll, duration)
  }, options.delay)
}
